import { isTaggedChoice, type ChoiceValue } from "../answerJson";
import type { ValidationResult } from "../contracts";
import type { SpecifiedRowAnswer, SpecifyTarget } from "./types";

export type SpecifyRow = { id: string; customPrompt?: string };

export type RowLabelDraft = {
  specifiedRows?: Readonly<Record<string, string>>;
};

export const rowLabelDraft = (
  labels: Readonly<Record<string, string>> | undefined,
): RowLabelDraft => (labels === undefined ? {} : { specifiedRows: labels });

export const updateRowLabel = (
  labels: RowLabelDraft["specifiedRows"],
  rowId: string,
  value: string,
): Readonly<Record<string, string>> =>
  Object.assign(Object.create(null) as Record<string, string>, labels, {
    [rowId]: value,
  });

export const specifyTargetKey = (target: SpecifyTarget): string =>
  JSON.stringify(
    target.kind === "row-label"
      ? [target.kind, target.rowId]
      : [target.kind, target.rowId ?? null, target.optionId],
  );

const numericOptionId = (raw: number | string): number | undefined =>
  typeof raw === "number" ? raw : /^\d+$/.test(raw) ? Number(raw) : undefined;

export const restoreChoice = <OptionId extends number | string>(
  options: readonly {
    id: OptionId;
    disabled: boolean;
    customPrompt?: string;
  }[],
  raw: ChoiceValue | undefined,
): { optionId: OptionId; customText?: string } | undefined => {
  if (raw === undefined || raw === "") return undefined;
  if (isTaggedChoice(raw)) {
    const option =
      "custom" in raw
        ? options.find((item) => item.customPrompt !== undefined)
        : options.find((item) => item.id === raw.option);
    if (!option || option.disabled) return undefined;
    return "custom" in raw
      ? { optionId: option.id, customText: raw.custom }
      : { optionId: option.id };
  }
  const directOption = options.find((item) => item.id === raw);
  if (directOption && !directOption.disabled) {
    return { optionId: directOption.id };
  }
  const numericId = numericOptionId(raw);
  const option =
    numericId === undefined
      ? options.find((item) => item.customPrompt !== undefined)
      : options.find((item) => item.id === numericId);
  if (!option || option.disabled) return undefined;
  return numericId === undefined
    ? { optionId: option.id, customText: String(raw) }
    : { optionId: option.id };
};

export const isSpecifiedRowAnswer = (
  value: unknown,
): value is SpecifiedRowAnswer<unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  "answer" in value &&
  "specified_row" in value &&
  typeof value.specified_row === "string";

export const rowAnswer = <Answer>(
  value: Answer | SpecifiedRowAnswer<Answer>,
): Answer => (isSpecifiedRowAnswer(value) ? (value.answer as Answer) : value);

export const restoreRowLabels = (
  rows: readonly SpecifyRow[],
  columns: readonly (readonly unknown[])[],
): Readonly<Record<string, string>> => {
  const labels = Object.create(null) as Record<string, string>;
  rows.forEach((row, index) => {
    if (row.customPrompt === undefined) return;
    for (const column of columns) {
      const value = column[index];
      if (isSpecifiedRowAnswer(value)) {
        labels[row.id] = value.specified_row;
        break;
      }
    }
  });
  return labels;
};

export const validateRowLabels = (
  rows: readonly SpecifyRow[],
  labels: Readonly<Record<string, string>>,
): ValidationResult => {
  const missing = rows.find(
    (row) => row.customPrompt !== undefined && !labels[row.id]?.trim(),
  );
  return missing
    ? {
        valid: false,
        error: missing.customPrompt!,
        fieldId: specifyTargetKey({ kind: "row-label", rowId: missing.id }),
      }
    : { valid: true };
};

export const encodeRowAnswer = <Answer>(
  row: SpecifyRow,
  labels: Readonly<Record<string, string>>,
  answer: Answer,
): Answer | SpecifiedRowAnswer<Answer> =>
  row.customPrompt === undefined
    ? answer
    : { answer, specified_row: labels[row.id].trim() };
